/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { accessAPI, Titulo, Formulario, Boton, usePermiso } from "../shared";
import { Divider } from "primereact/divider";
import { confirmDialog } from "primereact/confirmdialog";
import { ROLES_ADMIN, ROL_COLABORADOR } from "../roles";

/**
 * Cuerpo del panel de gestión de accesos de un usuario. Lo usan la sección de
 * clientes y la de colaboradores, así que no dibuja su propio Sidebar ni su
 * propio Toast: los pone el que lo monta.
 *
 * Maneja los tres tipos de acceso: a empresas, como colaborador (se elige a
 * qué colaborador corresponde el usuario) y como administrador. Los de
 * administrador solo los puede dar o quitar quien gestiona administradores;
 * el resto los ve pero no los toca.
 *
 * @param {number} usuarioId usuario cuyos accesos se gestionan
 */
export default function GestionAccesos({
  usuarioId,
  toast,
  onAccesoEliminado,
  onUsuarioEliminado
}) {
  const [usuarioDatos, setUsuarioDatos] = useState(null);
  const [accesos, setAccesos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [accesoEnProceso, setAccesoEnProceso] = useState(null);
  const [loaderEmpresa, setLoaderEmpresa] = useState(false);
  const [loaderEliminar, setLoaderEliminar] = useState(false);
  const [formKeyEmpresa, setFormKeyEmpresa] = useState(0);
  const [loaderColaborador, setLoaderColaborador] = useState(false);
  const [loaderAdministrador, setLoaderAdministrador] = useState(false);
  // Opciones del dropdown de colaboradores y el id del acceso de colaborador
  // (el id de Acceso es distinto en cada organismo: se busca por accesotipo).
  const [colaboradores, setColaboradores] = useState(null);
  const [accesoColaboradorId, setAccesoColaboradorId] = useState(null);
  const puedeGestionarAdministradores = usePermiso(
    "admin.administradores"
  ).puedeEditar;

  useEffect(() => {
    if (!usuarioId) return;
    cargar();
    accessAPI(
      "GET",
      "admin/auditor",
      null,
      (respuesta) =>
        setColaboradores(
          respuesta
            .filter((c) => c.activo !== false)
            .map((c) => ({
              value: c.id,
              etiqueta: `${c.nombre} ${c.apellido}`
            }))
        ),
      (respuesta) => error(respuesta, "Error al cargar colaboradores")
    );
    accessAPI(
      "GET",
      "admin/usuario/acceso?asignables=true",
      null,
      (respuesta) =>
        setAccesoColaboradorId(
          respuesta.find((a) => a.accesotipo === ROL_COLABORADOR)?.value || null
        ),
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioId]);

  function error(respuesta, porDefecto) {
    toast?.current?.show({
      severity: "error",
      summary: "Error",
      detail: respuesta?.[0]?.msg || porDefecto,
      sticky: true
    });
  }

  function cargar() {
    setCargando(true);
    accessAPI(
      "GET",
      `admin/usuario/${usuarioId}/accesos`,
      null,
      (respuesta) => {
        setUsuarioDatos(respuesta.usuario);
        setAccesos(respuesta.accesos);
        setCargando(false);
      },
      (respuesta) => {
        setCargando(false);
        error(respuesta, "Error al cargar accesos");
      }
    );
  }

  const esAccesoAdmin = (acceso) =>
    ROLES_ADMIN.includes(acceso.acceso.accesotipo);

  function eliminarAcceso(acceso) {
    setAccesoEnProceso(acceso.id);
    accessAPI(
      "DELETE",
      esAccesoAdmin(acceso)
        ? `admin/administrador/${usuarioId}/acceso`
        : `admin/usuario/acceso/${acceso.id}`,
      null,
      (respuesta) => {
        setAccesoEnProceso(null);
        setAccesos((prev) => prev.filter((a) => a.id !== acceso.id));
        toast?.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta?.[0]?.msg || "Acceso eliminado"
        });
        if (onAccesoEliminado) onAccesoEliminado(acceso);
      },
      (respuesta) => {
        setAccesoEnProceso(null);
        error(respuesta, "Error al eliminar acceso");
      }
    );
  }

  function agregarAcceso(
    datos,
    setLoader,
    resetForm = () => {},
    endpoint = `admin/usuario/${usuarioId}/acceso`
  ) {
    setLoader(true);
    accessAPI(
      "POST",
      endpoint,
      datos,
      (respuesta) => {
        setLoader(false);
        resetForm();
        const nuevo = respuesta?.[0]?.usuario_x_acceso;
        if (nuevo) setAccesos((prev) => [...(prev || []), nuevo]);
        toast?.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta?.[0]?.msg || "Acceso agregado"
        });
      },
      (respuesta) => {
        setLoader(false);
        error(respuesta, "Error al agregar acceso");
      }
    );
  }

  function eliminarUsuario() {
    setLoaderEliminar(true);
    accessAPI(
      "DELETE",
      `admin/usuario/${usuarioId}`,
      null,
      (respuesta) => {
        setLoaderEliminar(false);
        toast?.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta?.[0]?.msg || "Usuario eliminado"
        });
        if (onUsuarioEliminado) onUsuarioEliminado(usuarioId);
      },
      (respuesta) => {
        setLoaderEliminar(false);
        error(respuesta, "Error al eliminar usuario");
      }
    );
  }

  const formEmpresa = {
    secciones: [
      {
        campos: [
          {
            nombre: "clienteid",
            tipoitemformularioid: 5,
            etiqueta: "Empresa",
            requerido: true,
            opciones: "admin/cliente",
            sinOpcionInicial: true
          },
          {
            nombre: "accesoid",
            tipoitemformularioid: 5,
            etiqueta: "Nivel de acceso",
            requerido: true,
            opciones: "admin/usuario/acceso?cliente=true",
            sinOpcionInicial: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar acceso",
        validar: true,
        callback: (datos) =>
          agregarAcceso(datos, setLoaderEmpresa, () =>
            setFormKeyEmpresa((k) => k + 1)
          )
      }
    ]
  };

  const formColaborador = {
    secciones: [
      {
        campos: [
          {
            nombre: "auditorid",
            tipoitemformularioid: 5,
            etiqueta: "Colaborador",
            requerido: true,
            opciones: colaboradores || [],
            sinOpcionInicial: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar acceso",
        validar: true,
        callback: (datos) =>
          agregarAcceso(
            { ...datos, accesoid: accesoColaboradorId },
            setLoaderColaborador
          )
      }
    ]
  };

  const formAdministrador = {
    secciones: [
      {
        campos: [
          {
            nombre: "accesotipo",
            tipoitemformularioid: 5,
            etiqueta: "Rol",
            requerido: true,
            opciones: "admin/administrador/roles",
            sinOpcionInicial: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar acceso",
        validar: true,
        callback: (datos) =>
          agregarAcceso(
            datos,
            setLoaderAdministrador,
            undefined,
            `admin/administrador/${usuarioId}/acceso`
          )
      }
    ]
  };

  // A qué apunta cada acceso: una empresa, un colaborador o la plataforma de
  // administración.
  const destinoDeAcceso = (acceso) =>
    acceso.cliente?.razonsocial ||
    (acceso.auditor
      ? `${acceso.auditor.nombre} ${acceso.auditor.apellido}`
      : esAccesoAdmin(acceso)
        ? "Administración"
        : "—");

  // Cada usuario es a lo sumo un colaborador y tiene un solo rol de
  // administrador: los formularios se ofrecen solo si todavía no lo tiene.
  const esColaborador = accesos?.some((a) => a.auditorid);
  const esAdministrador = accesos?.some(esAccesoAdmin);

  if (cargando) {
    return <p css={{ color: "var(--grisTextos)" }}>Cargando accesos...</p>;
  }
  if (!usuarioDatos) return null;

  return (
    <>
      <div css={{ marginBottom: "1.5em" }}>
        <div css={{ fontWeight: 600, fontSize: "1.05em" }}>
          {usuarioDatos.nombre} {usuarioDatos.apellido}
        </div>
        <div css={{ color: "var(--grisTextos)", fontSize: "0.9em" }}>
          {usuarioDatos.email}
        </div>
      </div>

      <Titulo>Accesos actuales</Titulo>
      {accesos && accesos.length === 0 && (
        <p css={{ color: "var(--grisTextos)" }}>
          Este usuario no tiene accesos registrados.
        </p>
      )}
      {accesos && accesos.length > 0 && (
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75em",
            marginBottom: "1em"
          }}
        >
          {accesos.map((acceso) => (
            <div
              key={acceso.id}
              css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1em",
                padding: "0.75em",
                border: "1px solid #e0e0e0",
                borderRadius: "6px"
              }}
            >
              <div css={{ minWidth: 0, flex: 1 }}>
                <div
                  css={{
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {destinoDeAcceso(acceso)}
                </div>
                <div css={{ color: "var(--grisTextos)", fontSize: "0.85em" }}>
                  {acceso.acceso.nombre}
                </div>
              </div>
              <div
                css={{ display: "flex", alignItems: "center", gap: "0.5em" }}
              >
                {accesoEnProceso === acceso.id && (
                  <i className="pi pi-spin pi-spinner" />
                )}
                {(!esAccesoAdmin(acceso) || puedeGestionarAdministradores) && (
                <Boton
                  texto
                  rojo
                  chico
                  disabled={accesoEnProceso === acceso.id}
                  callback={() =>
                    confirmDialog({
                      message: `¿Eliminar el acceso ${acceso.acceso.nombre} de ${destinoDeAcceso(acceso)}?`,
                      header: "Confirmar eliminación",
                      icon: "pi pi-exclamation-triangle",
                      acceptLabel: "Eliminar",
                      rejectLabel: "Cancelar",
                      acceptClassName: "p-button-danger",
                      accept: () => eliminarAcceso(acceso)
                    })
                  }
                >
                  <i className="pi pi-trash" />
                </Boton>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Divider />

      <Titulo>Agregar acceso a empresa</Titulo>
      <Formulario
        key={formKeyEmpresa}
        config={formEmpresa}
        loader={loaderEmpresa}
        formularioEnCuerpo
      />

      {!esColaborador && colaboradores && accesoColaboradorId && (
        <>
          <Divider />
          <Titulo>Agregar acceso como colaborador</Titulo>
          <Formulario
            config={formColaborador}
            loader={loaderColaborador}
            formularioEnCuerpo
          />
        </>
      )}

      {!esAdministrador && puedeGestionarAdministradores && (
        <>
          <Divider />
          <Titulo>Agregar acceso como administrador</Titulo>
          <Formulario
            config={formAdministrador}
            loader={loaderAdministrador}
            formularioEnCuerpo
          />
        </>
      )}

      <Divider />

      <div css={{ marginTop: "1.5em" }}>
        <Boton
          rojo
          ancho
          loader={loaderEliminar}
          callback={() =>
            confirmDialog({
              message: `¿Eliminar completamente al usuario ${usuarioDatos.nombre} ${usuarioDatos.apellido}? Se borrarán todos sus accesos y no podrá iniciar sesión.`,
              header: "Eliminar usuario",
              icon: "pi pi-exclamation-triangle",
              acceptLabel: "Eliminar usuario",
              rejectLabel: "Cancelar",
              acceptClassName: "p-button-danger",
              accept: () => eliminarUsuario()
            })
          }
        >
          Eliminar usuario completo
        </Boton>
      </div>
    </>
  );
}
